/**
 * Typed wrappers for the `/salescoach/api/operations/*` endpoints (Plan 08 Task 8.1).
 *
 * Mirrors `quality-api.ts` / `engagement-api.ts` conventions:
 *   1. `buildQuery(filters)` strips defaults + resolves preset → from/to.
 *   2. Granularity / limit / source are appended as extra params.
 *   3. Return type is always the raw `ApiEnvelope<T>` — unwrapping is the
 *      hook's job (`use-operations.ts`).
 *
 * Seven endpoints per Plan 03-04 backend:
 *   GET /kpis                  → OperationsKpis
 *   GET /cost-trend            → CostTrendPoint[]        (granularity)
 *   GET /latency-distribution  → LatencyDistributionData
 *   GET /error-breakdown       → ErrorBreakdownRow[]     (limit, default 20, max 100)
 *   GET /tools                 → ToolUsageRow[]          (limit, default 25, max 100)
 *   GET /skills                → SkillUsageRow[]
 *   GET /complexity            → ComplexityRow[]
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  ComplexityRow,
  CostTrendPoint,
  ErrorBreakdownRow,
  ErrorBucketTraceRow,
  JiraMetrics,
  LatencyDistributionData,
  OperationsKpis,
  SkillDetail,
  SkillUsageRow,
  ToolUsageRow,
} from './api-types';

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export async function getOperationsKpis(
  filters: Filters,
): Promise<ApiEnvelope<OperationsKpis>> {
  return apiFetch<ApiEnvelope<OperationsKpis>>(
    buildApiUrl('/salescoach/api/operations/kpis', filters),
  );
}

export async function getCostTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<CostTrendPoint[]>> {
  return apiFetch<ApiEnvelope<CostTrendPoint[]>>(
    buildApiUrl('/salescoach/api/operations/cost-trend', filters, { granularity }),
  );
}

export async function getLatencyDistribution(
  filters: Filters,
): Promise<ApiEnvelope<LatencyDistributionData>> {
  return apiFetch<ApiEnvelope<LatencyDistributionData>>(
    buildApiUrl('/salescoach/api/operations/latency-distribution', filters),
  );
}

/** /error-breakdown params — backend caps `limit` at 100, default 20. */
export interface ErrorBreakdownParams {
  limit?: number;
}

export async function getErrorBreakdown(
  filters: Filters,
  params: ErrorBreakdownParams = {},
): Promise<ApiEnvelope<ErrorBreakdownRow[]>> {
  return apiFetch<ApiEnvelope<ErrorBreakdownRow[]>>(
    buildApiUrl('/salescoach/api/operations/error-breakdown', filters, {
      limit: params.limit,
    }),
  );
}

/** /error-breakdown/:bucket/traces params — backend caps `limit` at 500, default 100. */
export interface ErrorBucketTracesParams {
  limit?: number;
}

export async function getErrorBucketTraces(
  bucket: string,
  filters: Filters,
  params: ErrorBucketTracesParams = {},
): Promise<ApiEnvelope<ErrorBucketTraceRow[]>> {
  return apiFetch<ApiEnvelope<ErrorBucketTraceRow[]>>(
    buildApiUrl(
      `/salescoach/api/operations/error-breakdown/${encodeURIComponent(
        bucket,
      )}/traces`,
      filters,
      { limit: params.limit },
    ),
  );
}

/** /tools params — backend caps `limit` at 100, default 25. */
export interface ToolUsageParams {
  limit?: number;
}

export async function getToolUsage(
  filters: Filters,
  params: ToolUsageParams = {},
): Promise<ApiEnvelope<ToolUsageRow[]>> {
  return apiFetch<ApiEnvelope<ToolUsageRow[]>>(
    buildApiUrl('/salescoach/api/operations/tools', filters, {
      limit: params.limit,
    }),
  );
}

export async function getSkillsUsage(
  filters: Filters,
): Promise<ApiEnvelope<SkillUsageRow[]>> {
  return apiFetch<ApiEnvelope<SkillUsageRow[]>>(
    buildApiUrl('/salescoach/api/operations/skills', filters),
  );
}

/** Single-skill detail — what data/TOPIC-TAXONOMY.yaml declares about the topic.
 *  No filters: the payload is the taxonomy entry, not trace-derived.
 *  encodeURIComponent the topicId in case it ever contains characters Express's
 *  path matcher would special-case. */
export async function getSkillDetail(
  topicId: string,
): Promise<ApiEnvelope<SkillDetail>> {
  return apiFetch<ApiEnvelope<SkillDetail>>(
    `/salescoach/api/operations/skills/${encodeURIComponent(topicId)}`,
  );
}

export async function getComplexityDistribution(
  filters: Filters,
): Promise<ApiEnvelope<ComplexityRow[]>> {
  return apiFetch<ApiEnvelope<ComplexityRow[]>>(
    buildApiUrl('/salescoach/api/operations/complexity', filters),
  );
}

/**
 * Jira prefill funnel — three numbers (generated / clicked / conversion)
 * mirrored from the Slack `/report` content-creation block. Same compute
 * helper (`computeJiraMetrics`) serves both surfaces.
 */
export async function getJiraMetrics(
  filters: Filters,
): Promise<ApiEnvelope<JiraMetrics>> {
  return apiFetch<ApiEnvelope<JiraMetrics>>(
    buildApiUrl('/salescoach/api/operations/jira-metrics', filters),
  );
}
